import {
  getSystemTimezone,
  normalizeDateInput,
  normalizeDateTimeInput,
  toFieldOutputValue
} from './date-field-utils'

const dateFieldRegistry = {}
const dateFieldListeners = []

function setRegistryValue (id, value) {
  if (!id) {
    return
  }

  dateFieldRegistry[id] = value || null
  dateFieldListeners.forEach(listener => listener(id, dateFieldRegistry[id]))
}

function addRegistryListener (listener) {
  dateFieldListeners.push(listener)

  return function removeRegistryListener () {
    const index = dateFieldListeners.indexOf(listener)
    if (index >= 0) {
      dateFieldListeners.splice(index, 1)
    }
  }
}

export default {
  name: 'st-date-field',
  inheritAttrs: false,
  props: {
    value: [String, Date, Number],
    label: String,
    disabled: Boolean,
    hideDetails: [Boolean, String],
    textFieldProps: {
      type: Object,
      default: () => ({})
    },
    clearable: {
      type: Boolean,
      default: true
    },
    id: String,
    fromDateId: String,
    toDateId: String,
    min: [String, Date, Number],
    max: [String, Date, Number],
    returnDate: Boolean,
    timezone: String,
    withTime: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      linkedFromValue: null,
      linkedToValue: null,
      removeRegistryListener: null
    }
  },
  computed: {
    resolvedTimezone () {
      return this.$props.timezone || getSystemTimezone()
    },
    inputType () {
      return this.$props.withTime ? 'datetime-local' : 'date'
    },
    inputValue () {
      return this.normalizeForField(this.$props.value)
    },
    computedLabel () {
      return this.$props.label || (this.$props.textFieldProps && this.$props.textFieldProps.label) || null
    },
    fieldProps () {
      return Object.assign({}, this.$attrs || {}, this.$props.textFieldProps || {})
    },
    computedMin () {
      return this.normalizeForField(this.$props.min) || this.linkedFromValue || null
    },
    computedMax () {
      return this.normalizeForField(this.$props.max) || this.linkedToValue || null
    },
    appendIcon () {
      return this.$props.clearable && this.inputValue && !this.$props.disabled ? 'fal fa-times' : ''
    },
    forwardedListeners () {
      return Object.keys(this.$listeners || {}).reduce((result, key) => {
        if (key !== 'input' && key !== 'change' && key !== 'clear') {
          result[key] = this.$listeners[key]
        }

        return result
      }, {})
    }
  },
  watch: {
    value () {
      this.updateRegisteredValue()
    },
    timezone () {
      this.updateRegisteredValue()
    },
    withTime () {
      this.updateRegisteredValue()
    },
    id () {
      this.updateRegisteredValue()
    },
    fromDateId () {
      this.refreshLinkedValues()
    },
    toDateId () {
      this.refreshLinkedValues()
    }
  },
  mounted () {
    this.removeRegistryListener = addRegistryListener(this.onRegistryValueChange)
    this.refreshLinkedValues()
    this.updateRegisteredValue()
  },
  beforeDestroy () {
    if (this.removeRegistryListener) {
      this.removeRegistryListener()
    }

    if (this.$props.id && dateFieldRegistry[this.$props.id] === this.inputValue) {
      setRegistryValue(this.$props.id, null)
    }
  },
  methods: {
    normalizeForField (value) {
      if (this.$props.withTime) {
        return normalizeDateTimeInput(value, this.resolvedTimezone)
      }

      return normalizeDateInput(value, this.resolvedTimezone)
    },
    toOutputValue (value) {
      return toFieldOutputValue(value, {
        returnDate: this.$props.returnDate,
        withTime: this.$props.withTime,
        timezone: this.resolvedTimezone
      })
    },
    updateRegisteredValue () {
      setRegistryValue(this.$props.id, this.inputValue)
    },
    refreshLinkedValues () {
      this.linkedFromValue = this.$props.fromDateId ? dateFieldRegistry[this.$props.fromDateId] || null : null
      this.linkedToValue = this.$props.toDateId ? dateFieldRegistry[this.$props.toDateId] || null : null
    },
    onRegistryValueChange (id, value) {
      if (id === this.$props.fromDateId) {
        this.linkedFromValue = value || null
      }

      if (id === this.$props.toDateId) {
        this.linkedToValue = value || null
      }
    },
    emitInput (value) {
      const inputValue = value || null
      this.$emit('input', this.toOutputValue(inputValue))
      setRegistryValue(this.$props.id, inputValue)
    },
    onInput (value) {
      this.emitInput(value)
    },
    onChange (value) {
      this.$emit('change', this.toOutputValue(value || null))
    },
    clearValue () {
      this.emitInput(null)
      this.$emit('change', null)
      this.$emit('clear')
    }
  }
}
